import { type Module } from "vuex";
import type {
  CandidateFullResponse,
  CommunicationRelayDraft,
  CandidatePatchRequest,
  CandidateRequest,
  DefaultApi,
  HiringStageOfferRequest,
  HiringStageResponse,
  PhoneNumberLookupResponse,
} from "@/api";
import { Candidate, CandidateFull } from "@/models/candidate";
import { CandidateApplication } from "@/models/candidate_application";
import { CandidateApplications } from "@/models/candidate_applications";
import { CandidateSyncStatus } from "@/models/candidate_sync_status";
import { Candidates } from "@/models/candidates";
import type { State as RootState } from "@/stores";
export interface State {
  candidatesByPosition: Record<number, Candidates>;
  listLoadingByPosition: Record<number, boolean>;
  syncStatusesByPosition: Record<number, CandidateSyncStatus>;
  candidateApplications?: CandidateApplications;
}

export interface CandidateBulkCreateResult {
  candidates: Candidates;
  createdCount: number;
  skippedCount: number;
}
export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    candidatesByPosition: {},
    listLoadingByPosition: {},
    syncStatusesByPosition: {},
    candidateApplications: undefined,
  },
  getters: {
    candidatesForPosition:
      (state) =>
      (positionId: number): Candidates | undefined => {
        return state.candidatesByPosition[positionId];
      },
    allCandidates(state): Candidates {
      return Object.values(state.candidatesByPosition).reduce<Candidates>(
        (all, candidates) => all.append(candidates),
        new Candidates(),
      );
    },
  },
  actions: {
    async getGlobalCandidates() {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getGlobalCandidates();
      return new Candidates(
        response.data.map((candidate) => new CandidateFull(candidate)),
      );
    },
    async getCandidates({ commit }, positionId: number) {
      commit("setListLoading", { positionId, loading: true });
      try {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getCandidates(positionId);
        const candidates = new Candidates(
          response.data.map(
            (candidate: Partial<CandidateFullResponse>) =>
              new CandidateFull(candidate),
          ),
        );
        commit("setCandidates", {
          positionId,
          candidates,
        });
        return candidates;
      } finally {
        commit("setListLoading", { positionId, loading: false });
      }
    },
    async getCandidate(
      { commit },
      {
        positionId,
        candidateId,
        // A read-only fetch passes cache: false so it doesn't overwrite the underlying view's cached row; defaults true for the list/detail callers.
        cache = true,
      }: {
        positionId: number;
        candidateId: number;
        cache?: boolean;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getCandidate(positionId, candidateId);
      const candidate = new Candidate(response.data);
      if (cache)
        commit("setCandidate", {
          positionId,
          candidate,
        });
      return candidate;
    },
    async lookupPhoneNumber(
      {},
      {
        phoneNumber,
      }: {
        phoneNumber: string;
      },
    ): Promise<PhoneNumberLookupResponse> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getPhoneNumberLookup(phoneNumber);
      return response.data;
    },
    async postCandidate(
      { commit },
      {
        positionId,
        candidateRequest,
      }: {
        positionId: number;
        candidateRequest: CandidateRequest;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCandidate(positionId, candidateRequest);
      const candidate = new Candidate(response.data);
      commit("setCandidate", {
        positionId,
        candidate,
      });
      commit("statistics/invalidateStatistics", null, { root: true });
      commit("position/invalidatePositions", null, { root: true });
      return candidate;
    },
    async postCandidateBulk(
      { commit },
      {
        positionId,
        candidateRequests,
      }: {
        positionId: number;
        candidateRequests: CandidateRequest[];
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCandidateBulk(
        positionId,
        candidateRequests,
      );
      const candidates = new Candidates(
        response.data.candidates.map(
          (candidate: Partial<CandidateFullResponse>) =>
            new CandidateFull(candidate),
        ),
      );
      commit("appendCandidates", {
        positionId,
        candidates,
      });
      if (response.data.created_count > 0) {
        commit("statistics/invalidateStatistics", null, { root: true });
        commit("position/invalidatePositions", null, { root: true });
      }
      return {
        candidates,
        createdCount: response.data.created_count,
        skippedCount: response.data.skipped_count,
      } satisfies CandidateBulkCreateResult;
    },
    async patchCandidate(
      { commit },
      {
        positionId,
        candidateId,
        candidateRequest,
        // cache: false skips committing the response to the list store: a card opened over another view returns a plain
        // `Candidate` that lacks fields (hiring stage, evaluation, category) that view's cached row carries.
        cache = true,
      }: {
        positionId: number;
        candidateId: number;
        candidateRequest: CandidatePatchRequest;
        cache?: boolean;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.patchCandidate(
        positionId,
        candidateId,
        candidateRequest,
      );
      const candidate = new Candidate(response.data);
      if (cache)
        commit("setCandidate", {
          positionId,
          candidate,
        });
      if ("phone_number" in candidateRequest || "call_time" in candidateRequest)
        commit("statistics/invalidateStatistics", null, { root: true });
      if ("archived" in candidateRequest || "withdrawn" in candidateRequest)
        commit("position/invalidatePositions", null, { root: true });
      return candidate;
    },
    // Returns the stage the candidate landed in; the caller refetches the candidate list, since the stage tabs derive from it.
    async putCandidateOffer(
      {},
      {
        positionId,
        candidateId,
        offer,
      }: {
        positionId: number;
        candidateId: number;
        offer: HiringStageOfferRequest;
      },
    ): Promise<HiringStageResponse> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as DefaultApi;
      const response = await api.putCandidateOffer(
        positionId,
        candidateId,
        offer,
      );
      return response.data;
    },
    // Separate from putCandidateOffer because the row toggle also reaches candidates with no offer, or ones past editing it.
    async patchCandidateTracked(
      {},
      {
        positionId,
        candidateId,
        tracked,
      }: {
        positionId: number;
        candidateId: number;
        tracked: boolean;
      },
    ): Promise<HiringStageResponse> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as DefaultApi;
      const response = await api.patchCandidateTracked(
        positionId,
        candidateId,
        {
          tracked,
        },
      );
      return response.data;
    },
    async getSyncCandidateStatus({ commit }, positionId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getSyncCandidateStatus(positionId);
      const syncStatus = new CandidateSyncStatus(response.data);
      commit("setSyncStatus", {
        positionId,
        syncStatus,
      });
      return syncStatus;
    },
    // Claims this recruiter's relay address for the candidate, so it writes on the server; null means the
    // deployment has no relay configured and the action should stay hidden.
    async postCandidateRelayDraft(
      {},
      {
        positionId,
        candidateId,
      }: {
        positionId: number;
        candidateId: number;
      },
    ): Promise<CommunicationRelayDraft | null> {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postCandidateRelayDraft(
        positionId,
        candidateId,
      );
      return response.data.draft;
    },
    async postCandidateSync({}, positionId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      await api.postCandidateSync(positionId);
      return;
    },
    async getCandidatePreviousApplications(
      { commit },
      {
        positionId,
        candidateId,
      }: {
        positionId: number;
        candidateId: number;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getCandidatePreviousApplications(
        positionId,
        candidateId,
      );
      const candidateApplications = new CandidateApplications(
        response.data.map(
          (candidateApplication: Partial<CandidateApplication>) =>
            new CandidateApplication(candidateApplication),
        ),
      );
      commit("setCandidateApplications", candidateApplications);
      return candidateApplications;
    },
  },
  mutations: {
    setListLoading(
      state,
      { positionId, loading }: { positionId: number; loading: boolean },
    ) {
      state.listLoadingByPosition = {
        ...state.listLoadingByPosition,
        [positionId]: loading,
      };
    },
    setCandidate(
      state,
      {
        positionId,
        candidate,
      }: {
        positionId: number;
        candidate: CandidateFull;
      },
    ) {
      const existing = state.candidatesByPosition[positionId];
      if (existing === undefined)
        state.candidatesByPosition = {
          ...state.candidatesByPosition,
          [positionId]: new Candidates([candidate]),
        };
      else
        state.candidatesByPosition = {
          ...state.candidatesByPosition,
          [positionId]: existing.update(candidate),
        };
    },
    setCandidates(
      state,
      {
        positionId,
        candidates,
      }: {
        positionId: number;
        candidates: Candidates;
      },
    ) {
      state.candidatesByPosition = {
        ...state.candidatesByPosition,
        [positionId]: candidates,
      };
    },
    setSyncStatus(
      state,
      {
        positionId,
        syncStatus,
      }: {
        positionId: number;
        syncStatus: CandidateSyncStatus;
      },
    ) {
      state.syncStatusesByPosition = {
        ...state.syncStatusesByPosition,
        [positionId]: syncStatus,
      };
    },
    appendCandidates(
      state,
      {
        positionId,
        candidates,
      }: {
        positionId: number;
        candidates: Candidates;
      },
    ) {
      const existing = state.candidatesByPosition[positionId];
      if (existing === undefined)
        state.candidatesByPosition = {
          ...state.candidatesByPosition,
          [positionId]: candidates,
        };
      else
        state.candidatesByPosition = {
          ...state.candidatesByPosition,
          [positionId]: existing.append(candidates),
        };
    },
    setCandidateApplications(
      state,
      candidateApplications: CandidateApplications,
    ) {
      state.candidateApplications = candidateApplications;
    },
  },
};
